import {
  type BombeoCompletion,
  bombeoCompletion,
  type BombeoMetrics,
  bombeoMetrics,
  type Muelle,
  type PlaceEvent,
  type ScaleEvent,
  sortEventsDesc,
} from '../domain/scales'
import { scaleTimeline } from './timeline'
import type { EventRow, MarpolAnnex, ScaleKey, ScaleRow, ScaleWithEventRows } from './types'

/** React Admin id of a scale: `<year>-<scaleId>`, e.g. `2026-86802`. */
export const scaleRecordId = ({ year, scaleId }: ScaleKey): string => `${String(year)}-${scaleId}`

export const parseScaleRecordId = (id: string): ScaleKey | undefined => {
  const match = /^(\d{4})-(.+)$/.exec(id)
  return match ? { year: Number(match[1]), scaleId: match[2] ?? '' } : undefined
}

/**
 * The place event behind `currentMuelle` (see the domain rule): the one in
 * progress at `now`, newest start first, otherwise the last one that started.
 * Kept in step with `currentMuelle` by a test.
 */
export const currentPlaceEvent = (events: readonly ScaleEvent[], now: number): PlaceEvent | undefined => {
  const started = sortEventsDesc(events.filter((e): e is PlaceEvent => e.type === 'PLACE' && Date.parse(e.startTime) <= now))
  return started.find(e => e.endTime === null || Date.parse(e.endTime) >= now) ?? started[0]
}

/**
 * Closed: the vessel has left for good. It has berth stays and every one has
 * ended at or before `now`; MARPOL and BOMBEO events can no longer be added.
 * A scale with no berth stay yet is open.
 */
export const isClosed = (events: readonly ScaleEvent[], now: number): boolean => {
  const stays = events.filter(e => e.type === 'PLACE')
  return stays.length > 0 && stays.every(e => e.endTime !== null && Date.parse(e.endTime) <= now)
}

/**
 * Where a scale stands: FINISHED once archived, whatever its berth stays say;
 * otherwise PLANNED until one of its berth stays starts, then IN_PROGRESS
 * until it is closed (see `isClosed`), then CLOSED.
 */
export type ScaleState = 'PLANNED' | 'IN_PROGRESS' | 'CLOSED' | 'FINISHED'

export const scaleState = ({ scale, events }: ScaleWithEventRows, now: number): ScaleState => {
  if (scale.finished) return 'FINISHED'
  if (isClosed(events, now)) return 'CLOSED'
  return events.some(e => e.type === 'PLACE' && Date.parse(e.startTime) <= now) ? 'IN_PROGRESS' : 'PLANNED'
}

/** Progress of a scale's BOMBEO events at `now`; the list's single pump icon. */
export type BombeoStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE'

/**
 * Same rule as bombeo-app (MARPOL and place events don't count):
 * DONE when it has BOMBEO events and every one has ended at or before `now`;
 * IN_PROGRESS when at least one has started and they haven't all ended;
 * NOT_STARTED otherwise (none has started, or there are none).
 */
export const bombeoStatus = (events: readonly ScaleEvent[], now: number): BombeoStatus => {
  const bombeos = events.filter(e => e.type === 'BOMBEO')
  const ended = (e: ScaleEvent): boolean => e.endTime !== null && Date.parse(e.endTime) <= now
  if (bombeos.length > 0 && bombeos.every(ended)) return 'DONE'
  if (bombeos.some(e => Date.parse(e.startTime) <= now)) return 'IN_PROGRESS'
  return 'NOT_STARTED'
}

/** Where one event stands at `now`: not started yet, under way, or ended. */
export type EventProgress = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE'

/**
 * A MARPOL/BOMBEO is DONE once both its start and end are recorded; a berth
 * stay once its end has passed. Otherwise NOT_STARTED before its start and
 * IN_PROGRESS from then on.
 */
export const eventProgress = (event: ScaleEvent, now: number): EventProgress => {
  if (event.endTime !== null && (event.type !== 'PLACE' || Date.parse(event.endTime) <= now)) return 'DONE'
  return Date.parse(event.startTime) > now ? 'NOT_STARTED' : 'IN_PROGRESS'
}

/** One circle of the list's Eventos and one row of the scale's Gantt chart: a berth stay or a MARPOL/BOMBEO. */
export interface EventMarker {
  id: string
  type: ScaleEvent['type']
  /** The stay's muelle; null on a MARPOL/BOMBEO. */
  muelle: Muelle | null
  marpolAnnex: MarpolAnnex | null
  /** A MARPOL/BOMBEO's number within its type, by start; null on a stay. */
  number: number | null
  /** A BOMBEO's exact product and tonnes, which name it when set; null on other events. */
  product: string | null
  quantityT: number | null
  /** ISO-8601. */
  startTime: string
  endTime: string | null
  progress: EventProgress
}

/**
 * The scale's events in the timeline's order (see `scaleTimeline`): each berth
 * stay followed by the MARPOL/BOMBEO events that start during it, by start.
 * The list's Eventos circles and the scale page's Gantt rows (see `ganttChart`).
 */
export const eventMarkers = (events: readonly EventRow[], now: number): EventMarker[] =>
  scaleTimeline(events).flatMap(column =>
    column.entries
      .filter(entry => entry.kind === 'ARRIVAL' || entry.kind === 'START')
      .map(({ event, number }) => ({
        id: event.id,
        type: event.type,
        muelle: event.type === 'PLACE' ? event.muelle : null,
        marpolAnnex: event.marpolAnnex,
        number,
        product: event.type === 'BOMBEO' ? event.product : null,
        quantityT: event.type === 'BOMBEO' ? event.quantityT : null,
        startTime: event.startTime,
        endTime: event.endTime,
        progress: eventProgress(event, now),
      })),
  )

/** A scale as the admin lists and shows it. */
export interface ScaleRecord extends ScaleRow {
  /** `<year>-<scaleId>`; see `scaleRecordId`. */
  id: string
  currentMuelle: Muelle | null
  /** Start/end of the place event that makes `currentMuelle`; null when the scale has none. */
  currentStartTime: string | null
  currentEndTime: string | null
  /** See `bombeoStatus`. */
  bombeoStatus: BombeoStatus
  /** See `eventMarkers`. */
  eventMarkers: EventMarker[]
  /** Inicio, Rendimiento and Salida; see `bombeoMetrics`. */
  bombeoMetrics: BombeoMetrics
  /** The Completo verdict on `bombeoMetrics`; see `bombeoCompletion`. */
  bombeoCompletion: BombeoCompletion
  /** Terminals operating across the scale's berth stays (codes, e.g. TEPSA), in stay order, without repeats. */
  terminals: string[]
  /** See `ScaleState`. */
  state: ScaleState
  /** See `isClosed`. */
  closed: boolean
}

/** An event as the admin edits it: the stored event plus its scale's React Admin id. */
export type EventRecord = EventRow & { scaleRecordId: string }

export const toEventRecord = (event: EventRow): EventRecord => ({
  ...event,
  scaleRecordId: scaleRecordId({ year: event.year, scaleId: event.scaleId }),
})

export const summarise = (found: ScaleWithEventRows, now: number): ScaleRecord => {
  const { scale, events } = found
  const current = currentPlaceEvent(events, now)
  const metrics = bombeoMetrics(events)
  return {
    ...scale,
    id: scaleRecordId(scale),
    currentMuelle: current?.muelle ?? null,
    currentStartTime: current?.startTime ?? null,
    currentEndTime: current?.endTime ?? null,
    bombeoStatus: bombeoStatus(events, now),
    eventMarkers: eventMarkers(events, now),
    bombeoMetrics: metrics,
    bombeoCompletion: bombeoCompletion(metrics),
    terminals: [
      ...new Set(
        sortEventsDesc(events)
          .reverse()
          .flatMap(event => (event.type === 'PLACE' ? event.terminals : [])),
      ),
    ],
    state: scaleState(found, now),
    closed: isClosed(events, now),
  }
}
